// Third party libraries
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { Icon, Image, List, Modal, Reveal, RevealContent } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import { closeLoader, openLoader } from '../../../../redux-store/loader';

// Styled components
import OButton from '../../../styled/button';

// Models
import SecurityModel from '../../../../models/security';
import { LicenseInterface } from '../../../../models/payment';

// Types
import type { AffiliationRequest } from '../../../../models/security';

// Locales
import I18n from '../../../../i18n';

// Assets
import DemoImage from '../../../assets/img/yogabot_icon.svg';

// Constants
import modalities from '../../../components/form/modalities/options';

// Styles
import './affiliationRequests.scss';

type Action = "deny" | "approve";

type Props = {
    key?: string;
    open: boolean;
    currentStudentsCount: number;
    onClose: () => void
};

const AffiliationRequests = ({ open, currentStudentsCount, onClose }: Props) => {

    const dispatch = useDispatch();
    const currentLicense = useSelector((state: { license: { currentLicense: LicenseInterface } }) => state.license.currentLicense);

    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState<AffiliationRequest[]>([]);
    const [admitsNew, setAdmitsNew] = useState(false);
    const [requestDetails, setRequestDetails] = useState<AffiliationRequest | null>(null);

    const getData = useCallback(async () => {

        setLoading(true);

        try {

            const requests = await SecurityModel.getUserProAffiliationRequests();

            setRequests(requests);

        } catch (error) {

            console.error(error);

        } finally {

            setLoading(false);

        }

    }, []);

    useEffect(() => {
        if (open === true) {
            getData();
        }
    }, [getData, open]);

    useEffect(() => {
        const maxStudents = currentLicense.maxStudents;
        const isFree = currentLicense.name === 'Free';

        // 1. ¿El nuevo estudiante supera el límite actual?
        const willExceedLimit = (currentStudentsCount + 1) > maxStudents;

        // 2. Aplicamos la regla de negocio:
        // - Si es Free: NO puede exceder (admitStudents será false si se pasa de 3).
        // - Si NO es Free: SÍ puede exceder (porque disparará el upgrade automático).
        const admitStudents = isFree ? !willExceedLimit : true;

        setAdmitsNew(admitStudents);

    }, [currentLicense.maxStudents, currentLicense.name, currentStudentsCount])

    const onAction = async (action: Action, userId: string) => {
        if (!admitsNew) return;

        const data = {
            action,
            userId
        }

        dispatch(openLoader());

        try {

            await SecurityModel.userProAffiliationRequestAction(data);

            getData();

        } catch (e) {

            console.error("Error:", e);

        } finally {

            dispatch(closeLoader());

        }

    }

    return (
        <Modal className='affiliationRequestModal' open={open} size='mini' closeIcon onClose={onClose}>
            <Modal.Header>{I18n.t('students.affiliationRequest.title')}</Modal.Header>
            <Modal.Content scrolling>
                {
                    requestDetails ?
                        <div className='requestDetails'>
                            <OButton
                                $primary
                                onClick={() => setRequestDetails(null)}
                            >
                                <Icon name='arrow left' />
                                <span>{I18n.t('students.affiliationRequest.backToList')}</span>
                            </OButton>
                            <div className='requestDetailsHeader'>
                                <Image size='medium' src={requestDetails.user.pictureUrl || DemoImage} onError={(e: any) => { e.target.src = DemoImage }} />
                                <div className='requestDetailsHeaderInfo'>
                                    <span className='requestDetailsInfo requestDetailsName'>{requestDetails.user?.name} {requestDetails.user?.surname}</span>
                                    <span className='requestDetailsInfo requestDetailsDate'>{I18n.t('students.affiliationRequest.createdAt')} {moment(requestDetails.createdAt).format('DD/MM/YYYY')}</span>
                                    <span className='requestDetailsInfo requestDetailsStatus'>{I18n.t(`students.affiliationRequest.${requestDetails.status}`)}</span>
                                    <span className='requestDetailsInfo requestDetailModality'>{I18n.t('students.affiliationRequest.modality')} {modalities[requestDetails.modality as keyof typeof modalities]}</span>
                                </div>
                            </div>
                            <div className='requestDetailsMessage'>
                                <h4>{I18n.t('students.affiliationRequest.messageTitle')}</h4>
                                <p>{requestDetails.message}</p>
                            </div>
                        </div>
                        : <div>
                            {
                                loading ? <span className="labeling">{I18n.t('loaders.loading')}...</span> : <List>
                                    {requests.map((request, index) =>
                                        <List.Item key={request.userId + index} className='requestItem' >
                                            <Reveal animated='fade' onClick={() => setRequestDetails(request)}>
                                                <RevealContent visible>
                                                    <Image avatar size='tiny' src={request.user.pictureUrl || DemoImage} onError={(e: any) => { e.target.src = DemoImage }} />
                                                </RevealContent>
                                                <RevealContent hidden>
                                                    <div className='showDetails'>
                                                        <Image avatar size='tiny' src={request.user.pictureUrl || DemoImage} onError={(e: any) => { e.target.src = DemoImage }} />
                                                        <span>{I18n.t('students.affiliationRequest.showDetails')}</span>
                                                    </div>
                                                </RevealContent>
                                            </Reveal>
                                            <div className='requestItemContent' title={!admitsNew ? I18n.t('students.affiliationRequest.noAdmitsNew') : undefined}>
                                                <span className='requestItemName'>{request.user?.name} {request.user?.surname}</span>
                                                <span className='requestItemDate'>{I18n.t('students.affiliationRequest.createdAt')} {moment(request.createdAt).format('DD/MM/YYYY')}</span>
                                                {
                                                    request.status === 'pending' ? <div className='requestItemActions'>
                                                        <OButton onClick={() => onAction('deny', request.userId)}>{I18n.t('students.affiliationRequest.deny')}</OButton>
                                                        <OButton
                                                            $terciary
                                                            $color="#fff"
                                                            disabled={!admitsNew}
                                                            onClick={() => onAction('approve', request.userId)}
                                                        >
                                                            {I18n.t('students.affiliationRequest.approve')}
                                                        </OButton>
                                                    </div> :
                                                        <div>
                                                            <span className="labeling">{I18n.t(`students.affiliationRequest.${request.status}`)}</span>
                                                        </div>
                                                }

                                            </div>
                                        </List.Item>
                                    )}
                                </List>
                            }
                        </div>
                }
            </Modal.Content>
        </Modal>
    );

}

export default AffiliationRequests;